import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Gift, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/hooks/useTenant";
import { useCustomerCohortCounts } from "@/hooks/useCustomerCohortCounts";
import { supabase } from "@/integrations/supabase/client";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const THROTTLE_MS = 30 * 24 * 60 * 60 * 1000; // don't nag again for a month after dismiss

// Stored on tenants.settings (jsonb). Typed locally to avoid a shared-types rebuild.
type ReferralSettings = { referralCardDismissedUntil?: number } | null;

/**
 * Contextual referral prompt shown on the dashboard at an "aha" moment — a good
 * week: a new Google review or a new customer in the last 7 days (a win = good
 * time to ask). Dismissible and throttled (30 days) via a flag in tenants.settings.
 * Renders nothing unless there's a win and it isn't currently throttled.
 */
export const ReferralAhaCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentTenant, setCurrentTenant } = useTenant();
  // Neue Kunden dieser Woche -- dieselbe Zahl wie auf der Dashboard-Kachel,
  // fertig aus der Datenbank statt aus dem komplett geladenen Kundenstamm.
  const { thisWeekCustomers: newCustomersThisWeek } =
    useCustomerCohortCounts(currentTenant?.id || null);

  const tenantId = currentTenant?.id ?? null;
  const isConnected = !!currentTenant?.gbp_location_id;
  const dismissedUntil = (currentTenant?.settings as ReferralSettings)?.referralCardDismissedUntil ?? 0;
  const throttled = dismissedUntil > Date.now();

  // New Google reviews this week (only queried when GBP is connected).
  const { data: newReviewsThisWeek = 0 } = useQuery({
    queryKey: ["referralAhaReviews", tenantId],
    enabled: !!tenantId && isConnected && !throttled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gbp_reviews")
        .select("create_time")
        .eq("tenant_id", tenantId as string);
      if (error) throw error;
      const now = Date.now();
      return (data ?? []).filter(
        (r) => r.create_time && now - new Date(r.create_time).getTime() <= WEEK_MS,
      ).length;
    },
  });

  const hasWin = newReviewsThisWeek > 0 || newCustomersThisWeek > 0;
  if (throttled || !hasWin) return null;

  // Spell out *why* it was a strong week, combining whichever wins apply.
  const reasons: string[] = [];
  if (newCustomersThisWeek > 0)
    reasons.push(t("referral.ahaReasonCustomers", { count: newCustomersThisWeek }));
  if (newReviewsThisWeek > 0)
    reasons.push(t("referral.ahaReasonReviews", { count: newReviewsThisWeek }));
  const reason = reasons.join(` ${t("referral.ahaAnd")} `);

  const dismiss = async () => {
    if (!currentTenant) return;
    const nextSettings = {
      ...(currentTenant.settings as object | null),
      referralCardDismissedUntil: Date.now() + THROTTLE_MS,
    };
    // Optimistically hide, then persist.
    setCurrentTenant({ ...currentTenant, settings: nextSettings } as typeof currentTenant);
    await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from("tenants").update({ settings: nextSettings as any }).eq("id", currentTenant.id);
  };

  return (
    <Card className="rounded-2xl border-primary/20 bg-primary/5 shadow-none">
      <CardContent className="flex items-start gap-3 p-4 sm:gap-4 sm:p-5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:h-11 sm:w-11">
          <Gift className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground-strong">{t("referral.ahaTitle")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("referral.ahaBody", { reason })}
          </p>
          <Button className="mt-3" size="sm" onClick={() => navigate("/referrals")}>
            <Gift className="mr-2 h-4 w-4" />
            {t("referral.ahaCta")}
          </Button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("referral.ahaDismiss")}
          className="text-muted-foreground/60 transition-colors hover:text-muted-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
};

export default ReferralAhaCard;
